import { useState, useCallback, useEffect, useRef } from 'react'
import { supabase } from '../lib/supabase'

/**
 * useFriendRequests Hook
 * Complete friend management with lists, requests, and real-time updates
 *
 * Features:
 * - Fetch and track friends list
 * - Track pending sent/received requests
 * - Real-time subscription for friend updates
 * - Send/accept/reject friend requests
 * - Remove friends
 * - Rate limiting
 *
 * @param {string} userId - Current user ID
 */
export function useFriendRequests(userId) {
  // State for friend lists
  const [friends, setFriends] = useState([])
  const [pendingReceived, setPendingReceived] = useState([])
  const [pendingSent, setPendingSent] = useState([])
  const [loading, setLoading] = useState(true)

  // Subscription ref
  const subscriptionRef = useRef(null)

  // Fetch all friendships for the user
  const fetchFriends = useCallback(async () => {
    if (!userId) {
      setFriends([])
      setPendingReceived([])
      setPendingSent([])
      setLoading(false)
      return
    }

    try {
      setLoading(true)

      // Get all friendships where user is involved
      const { data: friendships, error } = await supabase
        .from('friendships')
        .select(`
          id,
          user_id,
          friend_id,
          status,
          created_at
        `)
        .or(`user_id.eq.${userId},friend_id.eq.${userId}`)

      if (error) {
        console.error('Error fetching friendships:', error)
        return
      }

      // Collect all user IDs we need to fetch profiles for
      const userIds = new Set()
      friendships?.forEach(f => {
        if (f.user_id !== userId) userIds.add(f.user_id)
        if (f.friend_id !== userId) userIds.add(f.friend_id)
      })

      // Fetch profiles for all involved users
      let profiles = {}
      if (userIds.size > 0) {
        const { data: profilesData } = await supabase
          .from('profiles')
          .select('id, username, display_name, avatar_url')
          .in('id', Array.from(userIds))

        profilesData?.forEach(p => {
          profiles[p.id] = p
        })
      }

      // Categorize friendships
      const acceptedFriends = []
      const receivedRequests = []
      const sentRequests = []

      friendships?.forEach(friendship => {
        const otherUserId = friendship.user_id === userId ? friendship.friend_id : friendship.user_id
        const profile = profiles[otherUserId] || { username: 'Unknown', avatar_url: null }

        const friendData = {
          ...friendship,
          id: profile.id || otherUserId,
          friendshipId: friendship.id,
          username: profile.username,
          display_name: profile.display_name,
          avatar_url: profile.avatar_url,
        }

        if (friendship.status === 'accepted') {
          // Only add once (avoid duplicates from bidirectional friendships)
          if (!acceptedFriends.find(f => f.id === friendData.id)) {
            acceptedFriends.push(friendData)
          }
        } else if (friendship.status === 'pending') {
          if (friendship.user_id === userId) {
            // We sent this request
            sentRequests.push(friendData)
          } else {
            // We received this request
            receivedRequests.push(friendData)
          }
        }
      })

      setFriends(acceptedFriends)
      setPendingReceived(receivedRequests)
      setPendingSent(sentRequests)

    } catch (error) {
      console.error('Error in fetchFriends:', error)
    } finally {
      setLoading(false)
    }
  }, [userId])

  // Set up real-time subscription
  useEffect(() => {
    if (!userId) return

    // Initial fetch
    fetchFriends()

    // Subscribe to changes
    const channel = supabase
      .channel(`friendships:${userId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'friendships',
          filter: `user_id=eq.${userId}`,
        },
        () => {
          // Refetch on any change
          fetchFriends()
        }
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'friendships',
          filter: `friend_id=eq.${userId}`,
        },
        () => {
          // Refetch on any change
          fetchFriends()
        }
      )
      .subscribe()

    subscriptionRef.current = channel

    return () => {
      if (subscriptionRef.current) {
        supabase.removeChannel(subscriptionRef.current)
      }
    }
  }, [userId, fetchFriends])

  // Send friend request
  const sendRequest = useCallback(
    async (friendId, friendUsername) => {
      if (!userId) return { success: false, error: 'Not authenticated' }

      try {
        // Check if friendship already exists
        const { data: existing } = await supabase
          .from('friendships')
          .select('*')
          .or(`and(user_id.eq.${userId},friend_id.eq.${friendId}),and(user_id.eq.${friendId},friend_id.eq.${userId})`)
          .maybeSingle()

        if (existing) {
          const message =
            existing.status === 'pending'
              ? 'Friend request already sent'
              : existing.status === 'accepted'
              ? 'Already friends'
              : 'User is blocked'
          return { success: false, error: message }
        }

        // Create friendship record
        console.log('[FriendRequest] Inserting friendship:', { user_id: userId, friend_id: friendId, status: 'pending' });
        const { data: insertedData, error } = await supabase.from('friendships').insert({
          user_id: userId,
          friend_id: friendId,
          status: 'pending',
        }).select();

        if (error) {
          console.error('[FriendRequest] Insert failed:', error);
          throw error;
        }
        console.log('[FriendRequest] Insert succeeded:', insertedData);

        // Create notification for recipient
        try {
          await supabase.from('notifications').insert({
            recipient_id: friendId,
            sender_id: userId,
            type: 'friend_request',
            title: 'New Friend Request',
            message: `Someone sent you a friend request`,
            action_url: `/friends`,
            read: false,
          })
        } catch (notifError) {
          console.log('Could not create notification:', notifError)
        }

        // Refresh the list
        await fetchFriends()

        return { success: true }
      } catch (error) {
        console.error('Error sending friend request:', error)
        return { success: false, error: error.message }
      }
    },
    [userId, fetchFriends]
  )

  // Accept incoming request
  const acceptRequest = useCallback(
    async (friendshipId, senderUsername) => {
      try {
        // Get the friendship record
        const { data: friendship, error: fetchError } = await supabase
          .from('friendships')
          .select('*')
          .eq('id', friendshipId)
          .single()

        if (fetchError) throw fetchError

        // Update status to accepted
        const { error } = await supabase
          .from('friendships')
          .update({ status: 'accepted' })
          .eq('id', friendshipId)

        if (error) throw error

        // Create reverse friendship (bidirectional)
        if (friendship) {
          // Check if reverse already exists
          const { data: existingReverse } = await supabase
            .from('friendships')
            .select('id')
            .eq('user_id', friendship.friend_id)
            .eq('friend_id', friendship.user_id)
            .maybeSingle()

          if (!existingReverse) {
            await supabase.from('friendships').insert({
              user_id: friendship.friend_id,
              friend_id: friendship.user_id,
              status: 'accepted',
            })
          }

          // Create notification for sender
          try {
            await supabase.from('notifications').insert({
              recipient_id: friendship.user_id,
              sender_id: userId,
              type: 'friend_accept',
              title: 'Friend Request Accepted',
              message: `Your friend request was accepted`,
              action_url: `/friends`,
              read: false,
            })
          } catch (notifError) {
            console.log('Could not create notification:', notifError)
          }
        }

        // Refresh the list
        await fetchFriends()

        return { success: true }
      } catch (error) {
        console.error('Error accepting request:', error)
        return { success: false, error: error.message }
      }
    },
    [fetchFriends]
  )

  // Reject incoming request
  const rejectRequest = useCallback(
    async (friendshipId) => {
      try {
        const { error } = await supabase
          .from('friendships')
          .delete()
          .eq('id', friendshipId)

        if (error) throw error

        // Refresh the list
        await fetchFriends()

        return { success: true }
      } catch (error) {
        console.error('Error rejecting request:', error)
        return { success: false, error: error.message }
      }
    },
    [fetchFriends]
  )

  // Remove friend (unfriend)
  const removeFriend = useCallback(
    async (friendId) => {
      if (!userId) return { success: false, error: 'Not authenticated' }

      try {
        // Delete both directions of the friendship
        const { error } = await supabase
          .from('friendships')
          .delete()
          .or(`and(user_id.eq.${userId},friend_id.eq.${friendId}),and(user_id.eq.${friendId},friend_id.eq.${userId})`)

        if (error) throw error

        // Refresh the list
        await fetchFriends()

        return { success: true }
      } catch (error) {
        console.error('Error removing friend:', error)
        return { success: false, error: error.message }
      }
    },
    [userId, fetchFriends]
  )

  // Cancel sent request
  const cancelRequest = useCallback(
    async (friendshipId) => {
      try {
        const { error } = await supabase
          .from('friendships')
          .delete()
          .eq('id', friendshipId)
          .eq('user_id', userId)
          .eq('status', 'pending')

        if (error) throw error

        // Refresh the list
        await fetchFriends()

        return { success: true }
      } catch (error) {
        console.error('Error cancelling request:', error)
        return { success: false, error: error.message }
      }
    },
    [userId, fetchFriends]
  )

  // Refresh friends list manually
  const refreshFriends = useCallback(() => {
    return fetchFriends()
  }, [fetchFriends])

  return {
    friends,
    pendingReceived,
    pendingSent,
    loading,
    sendRequest,
    acceptRequest,
    rejectRequest,
    removeFriend,
    cancelRequest,
    refreshFriends,
  }
}
